import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Palette, RefreshCcw, Layout, CheckCircle2, Type, HelpCircle } from "lucide-react";
import GameInstructionsModal from "./ui/GameInstructionsModal";

const DesignWorkshop = () => {
  const [gameState, setGameState] = useState("start"); // start, playing, success
  const [sliders, setSliders] = useState({ spacing: 50, radius: 50, weight: 50 });
  const [target, setTarget] = useState({ spacing: 80, radius: 20, weight: 70 });
  const [score, setScore] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);

  const initGame = () => {
    setTarget({
      spacing: Math.floor(Math.random() * 80) + 10,
      radius: Math.floor(Math.random() * 80) + 10,
      weight: Math.floor(Math.random() * 80) + 10
    });
    setSliders({ spacing: 50, radius: 50, weight: 50 });
    setGameState("playing");
  };

  const checkMatch = useCallback(() => {
    const diff = 
      Math.abs(sliders.spacing - target.spacing) +
      Math.abs(sliders.radius - target.radius) +
      Math.abs(sliders.weight - target.weight);
    
    if (diff < 15) {
      setGameState("success");
      setScore(s => s + 1);
    }
  }, [sliders, target]);

  useEffect(() => {
    if (gameState === "playing") {
      checkMatch();
    }
  }, [sliders, gameState, checkMatch]);

  return (
    <section id="workshop" className="py-24 relative overflow-hidden bg-[#0a0a0a]">
      <div className="container-xl px-6">
        <div className="flex flex-col mb-12">
          <div className="text-left mb-8">
            <Kicker index="UX-01" label="Design Lab" />
          </div>
          <div className="text-center">
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-[var(--cream)]">
              UI <span className="italic gold-text">Harmony</span>
            </h2>
            <p className="mt-4 text-[var(--cream-muted)] text-sm uppercase tracking-[0.2em] max-w-lg mx-auto">
              Align the design variables to achieve perfect visual balance.
            </p>
            <button 
              onClick={() => setShowInstructions(true)}
              className="mt-6 flex items-center gap-2 text-[var(--gold)]/60 hover:text-[var(--gold)] transition-colors mx-auto text-[10px] font-impact tracking-widest uppercase"
            >
              <HelpCircle size={14} />
              How to Play
            </button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-center bg-[var(--ink-2)] p-8 md:p-16 rounded-[40px] border border-[var(--rule)] relative shadow-2xl">
          
          {/* Visual Preview Area */}
          <div className="relative aspect-[4/5] md:aspect-square bg-black rounded-3xl overflow-hidden border border-[var(--rule)] flex items-center justify-center p-12">
            <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" />
            
            <div className="relative z-10 w-full space-y-6">
               <motion.div 
                 animate={{ 
                   padding: `${sliders.spacing / 4}px`,
                   borderRadius: `${sliders.radius}px`,
                   fontWeight: (sliders.weight * 8) + 100
                 }}
                 className="bg-[var(--gold)]/10 border border-[var(--gold)]/30 text-[var(--gold)] text-center text-2xl font-display transition-all"
               >
                 MERAKI
               </motion.div>
               <div className="grid grid-cols-2 gap-4">
                 <motion.div 
                   animate={{ borderRadius: `${sliders.radius}px` }}
                   className="h-20 bg-white/5 border border-white/10" 
                 />
                 <motion.div 
                   animate={{ borderRadius: `${sliders.radius}px` }}
                   className="h-20 bg-white/5 border border-white/10" 
                 />
               </div>
            </div>

            {/* HUD */}
            <div className="absolute bottom-6 inset-x-6 flex justify-between items-end">
              <div>
                <div className="text-[10px] text-[var(--muted)] uppercase tracking-widest mb-1">ACCURACY</div>
                <div className="text-2xl font-impact text-white">
                  {Math.max(0, 100 - Math.floor((Math.abs(sliders.spacing - target.spacing) + Math.abs(sliders.radius - target.radius) + Math.abs(sliders.weight - target.weight)) / 3))}%
                </div>
              </div>
              <div>
                <div className="text-[10px] text-[var(--muted)] uppercase tracking-widest mb-1">DESIGNS SHIPPED</div>
                <div className="text-2xl font-impact text-[var(--gold)]">{score}</div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="relative z-40">
            {gameState === "start" ? (
              <div className="text-center py-10 flex flex-col items-center">
                <Layout className="w-16 h-16 text-[var(--gold)] mb-8 opacity-50" />
                <h3 className="font-display text-3xl text-white mb-8 italic">Ready to Design?</h3>
                <button 
                  className="group relative px-12 py-5 rounded-full overflow-hidden transition-all duration-500 hover:scale-105"
                  onClick={initGame}
                >
                  <div className="absolute inset-0 bg-white/5 backdrop-blur-md border border-white/20 group-hover:border-[var(--gold)]/50 transition-all" />
                  <span className="relative z-10 text-[var(--gold)] font-impact text-xl tracking-widest uppercase">Enter Workshop</span>
                </button>
              </div>
            ) : (
              <div className="space-y-12">
                <SliderControl label="Spacing" value={sliders.spacing} onChange={(v) => setSliders({...sliders, spacing: v})} target={target.spacing} />
                <SliderControl label="Radius" value={sliders.radius} onChange={(v) => setSliders({...sliders, radius: v})} target={target.radius} />
                <SliderControl label="Font Weight" value={sliders.weight} onChange={(v) => setSliders({...sliders, weight: v})} target={target.weight} />
                
                <button 
                  className="w-full py-4 border border-[var(--rule)] rounded-full text-[var(--cream-muted)] hover:text-[var(--gold)] hover:border-[var(--gold)] transition-all uppercase tracking-[0.3em] text-[10px] flex items-center justify-center gap-3"
                  onClick={initGame}
                >
                  <RefreshCcw size={14} /> Randomize Target
                </button>
              </div>
            )}
          </div>

          <AnimatePresence>
            {gameState === "success" && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/95 backdrop-blur-xl z-50 rounded-[40px] flex flex-col items-center justify-center p-12 text-center"
              >
                <CheckCircle2 className="text-[var(--gold)] w-20 h-20 mb-8" />
                <h3 className="font-display text-5xl text-white mb-4">DESIGN <span className="italic gold-text">APPROVED</span></h3>
                <button className="btn-primary px-12" onClick={initGame}>Next Challenge</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <GameInstructionsModal 
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
        title="UI Harmony Lab"
        instructions={[
          { title: "Understand the Target", desc: "Look at the visual preview area. There's a hidden 'target' configuration you need to match to achieve harmony." },
          { title: "Adjust the Variables", desc: "Use the Spacing, Radius, and Font Weight sliders to change the layout and typography of the preview elements." },
          { title: "Monitor Accuracy", desc: "Check the 'Accuracy' meter in the HUD. Your goal is to get as close to 100% as possible by fine-tuning each variable." },
          { title: "Ship the Design", desc: "Once you hit over 95% accuracy, the design is automatically approved. Try to ship as many perfect designs as you can!" }
        ]}
      />
    </section>
  );
};

const SliderControl = ({ label, value, onChange, target }) => (
  <div className="space-y-4">
    <div className="flex justify-between items-end">
      <label className="text-[10px] uppercase tracking-[0.3em] text-[var(--gold)] font-impact">{label}</label>
      <span className="text-xl font-display text-white">{value}</span>
    </div>
    <div className="relative h-12 flex items-center">
      <div className="absolute w-px h-6 bg-white/20 z-0" style={{ left: `${target}%` }} />
      <input 
        type="range" min="0" max="100" value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full h-1 bg-[var(--rule)] appearance-none cursor-pointer accent-[var(--gold)] rounded-full outline-none"
      />
    </div>
  </div>
);

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default DesignWorkshop;
